import React, { useState, FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import NavigationMenu from "./NavigationMenu";
import { FavoritesSection } from "./FavoritesSection";
import SidebarUserProfile from "./SidebarUserProfile";
import { getSidebarMenuItems, getBottomMenuItems } from "../../constants/sidebarMenuItems";
import FlexibleImage from "../common/FlexibleImage";
import type { Session } from "../../modules/session";
import logoFull from "../../images/GR_LOGO_Primary.png";

import "./Topbar.css";

interface TopbarProps {
  location: any;
  session: Session;
  organization: any;
  currentUserId: string;
  firstName: string;
  onLogOut: () => void;
  onDeleteImpersonation: () => void;
}

const Topbar: FunctionComponent<TopbarProps> = ({
  location,
  session,
  organization,
  currentUserId,
  firstName,
  onLogOut,
  onDeleteImpersonation
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const menuItems = getSidebarMenuItems(session, organization);
  const bottomMenuItems = getBottomMenuItems(session, currentUserId);

  // Close dropdown when route changes
  React.useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const getLocationBaseRoute = () => {
    const pathname = location.pathname;

    // Handle home route
    if (pathname === "/" || pathname === "/home") {
      return "home";
    }

    const pathList = pathname.split("/");

    // Handle sub-routes for campaigns
    if (pathname.includes("/campaigns/")) {
      return "campaigns";
    }

    // Handle accounts routes
    if (pathname.includes("/accounts/")) {
      return "accounts";
    }

    // Handle other specific routes
    if (pathname.includes("/fundraisers")) {
      return "fundraisers";
    }

    if (pathname.includes("/donations")) {
      return "donations";
    }

    if (pathname.includes("/recurring")) {
      return "recurring";
    }

    if (pathname.includes("/sales")) {
      return "sales";
    }

    if (pathname.includes("/vip-program")) {
      return "vip-program";
    }

    if (pathname.includes("/events")) {
      return "events";
    }

    if (pathname.includes("/organizations")) {
      return "organizations";
    }

    if (pathname.includes("/tap-n-donate")) {
      return "tap-n-donate";
    }

    return pathList.length >= 2 ? pathList[1] : null;
  };

  const getActiveSubRoute = () => {
    const pathname = location.pathname;

    // Campaigns sub-routes
    if (pathname.includes("/campaigns/live")) return "campaigns-live";
    if (pathname.includes("/campaigns/complete")) return "campaigns-complete";
    if (pathname.includes("/campaigns/archived")) return "campaigns-archived";

    // Fundraisers sub-routes
    if (pathname.includes("/fundraisers/all")) return "fundraisers-all";
    if (pathname.includes("/fundraisers/active")) return "fundraisers-active";
    if (pathname.includes("/fundraisers/inactive")) return "fundraisers-inactive";

    // Recurring sub-routes
    if (pathname.includes("/recurring/all")) return "recurring-all";
    if (pathname.includes("/recurring/active")) return "recurring-active";
    if (pathname.includes("/recurring/paused")) return "recurring-paused";

    // Sales sub-routes
    if (pathname.includes("/sales/tickets")) return "sales-tickets";
    if (pathname.includes("/sales/merchandise")) return "sales-merchandise";
    if (pathname.includes("/sales") && !pathname.includes("/sales/")) return "sales-all";

    // Accounts sub-routes
    if (pathname.includes("/accounts/all")) return "accounts-all";
    if (pathname.includes("/accounts/admins")) return "accounts-admins";
    if (pathname.includes("/accounts/fundraisers")) return "accounts-fundraisers";
    if (pathname.includes("/accounts/donors")) return "accounts-donors";

    return null;
  };

  const baseRoute = getLocationBaseRoute();
  const activeSubRoute = getActiveSubRoute();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOverlayClick = () => {
    setIsDropdownOpen(false);
  };

  return (
    <>
      <div className="topbar">
        <Link to="/" className="topbar-logo-link">
          <FlexibleImage
            size="small"
            shape="square"
            imageUrl={logoFull}
            className="topbar-logo"
            alt="Grassrootz Logo"
            showPlaceholder={false}
          />
        </Link>

        <button className="topbar-menu-toggle" onClick={toggleDropdown}>
          <Icon name={isDropdownOpen ? "times" : "bars"} />
        </button>
      </div>

      {isDropdownOpen && (
        <div className="topbar-overlay" onClick={handleOverlayClick} />
      )}

      <div className={`topbar-dropdown ${isDropdownOpen ? "open" : ""}`}>
        <div className="topbar-dropdown-content">
          <NavigationMenu
            items={menuItems}
            currentRoute={baseRoute}
            activeSubRoute={activeSubRoute}
            session={session}
            currentUserId={currentUserId}
            currentPath={location.pathname}
          />

          {session.isSystemAdmin && (
            <FavoritesSection
              currentPath={location.pathname + location.search}
              isMinimized={false}
            />
          )}

          {bottomMenuItems.length > 0 && (
            <NavigationMenu
              items={bottomMenuItems}
              currentRoute={baseRoute}
              activeSubRoute={activeSubRoute}
              session={session}
              isBottomMenu={true}
              currentUserId={currentUserId}
              currentPath={location.pathname}
            />
          )}

          <SidebarUserProfile
            firstName={firstName}
            currentUserId={currentUserId}
            impersonated={session.impersonated}
            session={session}
            onLogOut={onLogOut}
            onDeleteImpersonation={onDeleteImpersonation}
            isMinimized={false}
          />
        </div>
      </div>
    </>
  );
};

export default Topbar;
